import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { format, parseISO } from "date-fns";
import type { TFunction } from "i18next";

import type { CareEventBilling, Status } from "api/schemas/CareEventBilling";
import type { Dialog } from "routes/admin-billing/hooks/useBillingState";
import { CPTCode } from "routes/dashboard/default/components/MedicalNotesTable/CPTCode";
import { Spinner } from "shared/atoms/Spinner";

interface CreateColumnsParams {
  filter: Status;
  isLoadingMedicalNote: boolean;
  selectedCareEvent: CareEventBilling | null;
  smallScreenProps: { minWidth?: number };
  t: TFunction;
  colors: {
    black: string;
    green: string;
    red: string;
  };
  onSelectCareEvent: (careEvent: CareEventBilling) => void;
  onSelectCareEventIDs: (ids: number[]) => void;
  onShowDialog: (value: Dialog) => void;
}

export function createColumns({
  filter,
  isLoadingMedicalNote,
  selectedCareEvent,
  smallScreenProps,
  t,
  colors,
  onSelectCareEvent,
  onSelectCareEventIDs,
  onShowDialog,
}: CreateColumnsParams): GridColDef<CareEventBilling>[] {
  const { black, green, red } = colors;

  let cols: GridColDef<CareEventBilling>[] = [
    { field: "patient_id", headerName: t("patients.id") as string, flex: 1, ...smallScreenProps },
    {
      field: "patient_name",
      headerName: t("dashboard.tables.notes.labels.patient") as string,
      flex: 1,
      ...smallScreenProps,
    },
    {
      field: "patient_birthday",
      headerName: t("form.date_of_birth") as string,
      flex: 1,
      valueFormatter: (value: string) => (value ? format(parseISO(value), "PP") : ""),
      minWidth: 100,
      ...smallScreenProps,
    },
    {
      field: "icd_codes",
      headerName: t("dashboard.tables.notes.labels.icd") as string,
      valueGetter: (_value, row) => row.icd_codes?.[0] ?? "",
      flex: 1,
      ...smallScreenProps,
    },
    {
      field: "cpt_codes",
      headerName: t("dashboard.tables.notes.labels.cpt") as string,
      renderCell: (params: GridRenderCellParams<CareEventBilling, string[] | undefined>) => (
        <CPTCode cptCode={params.value?.[0] ?? null} />
      ),
      flex: 1,
      ...smallScreenProps,
    },
    { field: "type", headerName: t("dashboard.tables.notes.labels.type") as string, flex: 1, ...smallScreenProps },
    {
      field: "therapist_name",
      headerName: t("dashboard.tables.notes.labels.assigned_therapist") as string,
      flex: 1,
      ...smallScreenProps,
    },
  ];

  if (filter === "pending") {
    cols = cols.concat([
      {
        field: "date_of_service",
        headerName: t("dashboard.tables.notes.labels.date_of_service") as string,
        valueFormatter: value => (value ? format(parseISO(value), "PP") : ""),
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "created_at",
        headerName: t("dashboard.tables.notes.labels.created_at") as string,
        valueGetter: (_value, row) => {
          const v = row.created_at;
          return v ? format(parseISO(v), "PP") : "";
        },
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "signed_at",
        headerName: t("dashboard.tables.notes.labels.signed_at") as string,
        valueGetter: (_value, row) => {
          const v = row.signed_at;
          return v ? format(parseISO(v), "PP") : "";
        },
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "_complete",
        headerName: t("dashboard.tables.notes.labels.complete") as string,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<CareEventBilling>) => (
          <IconButton
            aria-label={t("admin_billing.mark_as_completed", { count: 1 }) as string}
            onClick={() => {
              onSelectCareEventIDs([params.row.care_event_id]);
              onShowDialog({ show: true, status: "completed" });
            }}
          >
            <FontAwesomeIcon icon={faCircleCheck} color={green} style={{ height: "16px", width: "16px" }} />
          </IconButton>
        ),
        disableColumnMenu: true,
        disableExport: true,
        sortable: false,
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "_discard",
        headerName: t("dashboard.tables.notes.labels.discard") as string,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<CareEventBilling>) => (
          <IconButton
            aria-label={t("admin_billing.mark_as_discarded", { count: 1 }) as string}
            onClick={() => {
              onSelectCareEventIDs([params.row.care_event_id]);
              onShowDialog({ show: true, status: "discarded" });
            }}
          >
            <FontAwesomeIcon icon={faCircleXmark} color={red} style={{ height: "16px", width: "16px" }} />
          </IconButton>
        ),
        disableColumnMenu: true,
        disableExport: true,
        sortable: false,
        flex: 1,
        ...smallScreenProps,
      },
    ]);
  } else {
    cols = cols.concat([
      {
        field: "manager_name",
        headerName: t("dashboard.tables.notes.labels.handled_by") as string,
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "handled_at",
        headerName: t("dashboard.tables.notes.labels.handled_at") as string,
        valueGetter: (_value, row) => {
          const v = row.created_at;
          return v ? format(parseISO(v), "PP") : "";
        },
        flex: 1,
        ...smallScreenProps,
      },
    ]);
  }

  cols.push({
    field: "_view",
    headerName: t("dashboard.tables.notes.labels.view") as string,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<CareEventBilling>) => (
      <IconButton
        aria-label={t("dashboard.tables.notes.labels.view") as string}
        onClick={e => {
          e.stopPropagation();
          onSelectCareEvent(params.row);
        }}
        size="small"
      >
        {isLoadingMedicalNote && selectedCareEvent?.care_event_id === params.row.care_event_id ? (
          <Spinner style={{ height: "40px", width: "40px" }} />
        ) : (
          <FontAwesomeIcon icon={faSearch} color={black} style={{ height: "16px", width: "16px" }} />
        )}
      </IconButton>
    ),
    disableColumnMenu: true,
    disableExport: true,
    sortable: false,
    flex: 1,
    ...smallScreenProps,
  });

  return cols;
}
